import { LOCALSTORAGE_DATA_SPLIT } from '@/app/constants';
import { safeStorage } from './safe-storage';

interface CleanupOptions {
  keyPrefixes: string[];
}

interface StorageData {
  value: string | undefined;
  expiresAt?: string | undefined;
}

const parseAndCheckExpiry = (dataString: string): { shouldDelete: boolean; reason: string; data?: StorageData } => {
  if (!dataString) {
    return { shouldDelete: false, reason: 'missing data' };
  }
  const parts = dataString.split(LOCALSTORAGE_DATA_SPLIT);
  const data = {
    value: parts[0],
    expiresAt: parts[1],
  };

  if (parts.length < 2) {
    return { shouldDelete: true, reason: 'invalid data format', data };
  }

  const [, expiresAt] = parts;
  if (!expiresAt) {
    return {
      shouldDelete: true,
      reason: 'missing expiration',
      data,
    };
  }

  const expiresAtNum = Number(expiresAt);
  if (Number.isNaN(expiresAtNum)) {
    return {
      shouldDelete: true,
      reason: 'invalid expiration format',
      data,
    };
  }

  if (expiresAtNum <= Date.now()) {
    return {
      shouldDelete: true,
      reason: 'expired',
      data,
    };
  }

  return {
    shouldDelete: false,
    reason: 'valid',
    data,
  };
};

const cleanupExpiredLocalStorage = ({ keyPrefixes }: CleanupOptions) => {
  try {
    if (!safeStorage.available) {
      return;
    }

    const allKeys = [];
    for (let i = 0; i < safeStorage.length; i++) {
      const key = safeStorage.key(i);
      if (key) allKeys.push(key);
    }

    const targetKeys = allKeys.filter((key) => keyPrefixes.some((prefix) => key.startsWith(prefix)));

    targetKeys.forEach((key) => {
      try {
        const data = safeStorage.getItem(key);
        const { shouldDelete } = parseAndCheckExpiry(data || '');

        if (shouldDelete) {
          safeStorage.removeItem(key);
        }
      } catch {
        safeStorage.removeItem(key);
      }
    });
  } catch {}
};

export const initStorageCleanup = (keyPrefixes: string[]) => {
  setTimeout(() => {
    cleanupExpiredLocalStorage({ keyPrefixes });
  }, 1000);
};

export const getStorageKey = (prefix: string, id: string) => {
  return `${prefix}_${id}`;
};

export const getStorageValueWithExpiry = (value: string | number, expiresAt: number) => {
  return `${value}${LOCALSTORAGE_DATA_SPLIT}${expiresAt}`;
};

export const getStorageValue = (key: string, deleteOnExpired = false): StorageData | undefined => {
  const dataString = safeStorage.getItem(key) || '';
  const { data, shouldDelete } = parseAndCheckExpiry(dataString);
  if (shouldDelete && deleteOnExpired) {
    safeStorage.removeItem(key);
    return undefined;
  }
  return data;
};
